import React from "react";
import { Box, makeStyles } from "@material-ui/core";
import { Theme } from "@material-ui/core/styles";
import ArrowUpwardIcon from "@material-ui/icons/ArrowUpward";
import ArrowDownwardIcon from "@material-ui/icons/ArrowDownward";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import { tesaDarkGrey, tesaDarkRedError, tesaGreenPositive, tesaGrey1, tesaGrey6 } from "../../../styles/colors";
import formatNumber from "../../../functions/formatNumber.function";

type Valuation = TestRequestRaw.Schemas.Valuation;

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    backgroundColor: tesaGrey6,
    borderRadius: theme.spacing(1),
    padding: theme.spacing(2),
  },
  headline: {
    ...theme.typography.h5,
    color: tesaDarkGrey,
  },
  numberline: {
    display: "flex",
    alignItems: "baseline",
    marginTop: theme.spacing(2),
    height: "1.5rem",
  },
  number: {
    ...theme.typography.h4,
    color: tesaDarkGrey,
  },
  subnote: {
    ...theme.typography.subtitle2,
    transform: "translateY(-0.05rem)",
    color: tesaGrey1,
    marginLeft: theme.spacing(1),
  },
  iconWrapper: {
    transform: "translateY(0.1rem)",
    height: "1.25rem",
    width: "1.25rem",
    display: "flex",
    alignItems: "center",
    marginLeft: theme.spacing(1),
    marginRight: theme.spacing(1),
  },
  icon: {
    height: "1.25rem",
    width: "1.25rem",
  },
}));

function mapValuation(valuation: Valuation): React.CSSProperties {
  switch (valuation) {
    case "POSITIVE":
      return { color: tesaGreenPositive };
    case "NEGATIVE":
      return { color: tesaDarkRedError };
    default:
      return {};
  }
}

export interface KpiTileProps {
  key: string;
  aggregationType: string;
  performance: number | null;
  title: string;
  total: number | null;
  unit: string | null;
  valuation: Valuation;
  yearToDate: number | null;
}

export interface EmptyKpiTileProps {
  key: string;
  empty: true;
}

function KpiTile(props: KpiTileProps | EmptyKpiTileProps) {
  const classes = useStyles(props);

  const { empty } = props as EmptyKpiTileProps;
  if (empty) {
    return <Box className={classes.container} height="7.75rem" />;
  }

  const { aggregationType, performance, title, total, unit, valuation, yearToDate } = props as KpiTileProps;

  const icon = {
    className: classes.icon,
    style: mapValuation(valuation),
  };

  return (
    <Box className={classes.container}>
      <Box className={classes.headline}>{title}</Box>
      <Box className={classes.numberline}>
        <Box className={classes.number}>{formatNumber(total, 1, unit, " ", "N/A")}</Box>
        <Box className={classes.subnote}>{aggregationType}</Box>
      </Box>
      <Box className={classes.numberline}>
        <Box className={classes.number}>{formatNumber(yearToDate, 1, unit, " ", "N/A")}</Box>
        {(!!performance || performance === 0) && (
          <>
            <Box className={classes.iconWrapper}>
              {performance > 0 && <ArrowUpwardIcon {...icon} />}
              {performance < 0 && <ArrowDownwardIcon {...icon} />}
              {performance === 0 && <ArrowBackIcon {...icon} />}
            </Box>
            <Box className={classes.number}>{formatNumber(Math.abs(performance * 100), 0, "%", " ", "N/A")}</Box>
          </>
        )}
        <Box className={classes.subnote}>Year to Date</Box>
      </Box>
    </Box>
  );
}

export default KpiTile;
